import { useEffect } from "react";
import { ScrollView, TextInput, TouchableOpacity, View } from "react-native";

import produce from "immer";

import { ArrowLeft, Check } from "@smartrent/icons";
import { Button, Typography, useTheme } from "@smartrent/ui";

import { FeedbackIllustration } from "@/components/FeedbackIllustration";
import Header from "@/components/common/Header";
import { useAppState } from "@/context/AppState";
import useWindow from "@/hooks/window";
import { logEvent } from "@/utils/analytics";

export const initialFeedbackItems: FeedbackItems = {
  activeIndex: 0,
  items: [
    {
      question: "What went well?",
      answers: [
        "Accessing the tour page",
        "Finding the unit",
        "Unlocking the door",
        "Touring the unit",
        "Other",
      ],
      additional: "",
      selected: [],
    },
    {
      question: "What would make this tour better?",
      answers: [
        "Schedule tour ahead of time",
        "Interactive property map",
        "Access to community amenities",
        "View Floor plans",
        "Unit availability and pricing",
        "Neighborhood information",
        "Apply for lease",
        "Other",
      ],
      additional: "",
      selected: [],
    },
  ],
};

export type FeedbackAnswer = string;
export type FeedbackAnswers = Array<FeedbackAnswer>;
export type FeedbackItem = {
  question: string;
  answers: FeedbackAnswers;
  additional: string;
  selected: Array<number>;
};
export type FeedbackItems = {
  activeIndex: number;
  items: Array<FeedbackItem>;
};

const FeedbackModal = ({
  setVisible,
  tourFeedback,
  setTourFeedback,
  completed,
  setCompleted,
}: {
  setVisible: Function;
  tourFeedback: FeedbackItems;
  setTourFeedback: Function;
  completed: boolean;
  setCompleted: Function;
}) => {
  const { colors } = useTheme();
  const { height: windowHeight } = useWindow();

  // @ts-ignore - no unused var
  const [{ config }, _setState] = useAppState();

  const setAnswer = (questionIndex: number, answerIndex: number) => {
    setTourFeedback(
      produce(tourFeedback, (draft) => {
        if (draft.items[questionIndex].selected.includes(answerIndex)) {
          draft.items[questionIndex].selected = draft.items[
            questionIndex
          ].selected.filter((val) => val !== answerIndex);
        } else {
          draft.items[questionIndex].selected.push(answerIndex);
        }
      })
    );
  };

  const next = () => {
    setTourFeedback(
      produce(tourFeedback, (draft) => {
        draft.activeIndex++;
      })
    );
  };

  useEffect(() => {
    if (completed) {
      const goodFeedback = tourFeedback.items[0];
      goodFeedback.selected.map((key) => {
        const item =
          goodFeedback.answers[key] === "Other"
            ? `${goodFeedback.answers[key]}: ${goodFeedback.additional}`
            : goodFeedback.answers[key];

        logEvent({
          name: "Tours - Good Feedback",
          eventProperties: {
            Feedback_Item: item,
          },
        });
      });

      const featuresFeedback = tourFeedback.items[1];
      featuresFeedback.selected.map((key) => {
        const item =
          featuresFeedback.answers[key] === "Other"
            ? `${featuresFeedback.answers[key]}: ${featuresFeedback.additional}`
            : featuresFeedback.answers[key];

        logEvent({
          name: "Tours - Features Feedback",
          eventProperties: {
            Feedback_Item: item,
          },
        });
      });

      setTourFeedback(initialFeedbackItems);
    }
  }, [completed, setTourFeedback, tourFeedback]);

  const item = tourFeedback.items[tourFeedback.activeIndex];
  const isLastPage: boolean =
    tourFeedback.activeIndex + 1 === tourFeedback.items.length;
  const questionAnswered = item.selected.length !== 0;

  const handleAdditional = (activeIndex: any, value: string) => {
    setTourFeedback(
      produce(tourFeedback, (draft: any) => {
        draft.items[activeIndex].additional = value;
      })
    );
  };

  return (
    <>
      {completed ? (
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <View style={{ marginBottom: 24 }}>
            <FeedbackIllustration />
          </View>

          <Typography type="title2" style={{ marginBottom: 32 }}>
            Thanks for the feedback!
          </Typography>
          <Button
            size="large"
            onPress={() => {
              logEvent({
                name: "Tours - Back to Tour",
                eventProperties: { Property_Name: config.propertyName },
              });
              setVisible(false);
            }}
            style={{ width: 215 }}
          >
            BACK TO TOUR
          </Button>
        </View>
      ) : (
        <>
          <Header
            style={{
              paddingHorizontal: 16,
            }}
          >
            <TouchableOpacity
              onPress={() => setVisible(false)}
              style={{ flexDirection: "row", alignItems: "center" }}
              accessibilityRole="link"
            >
              <ArrowLeft color={colors.white} />
              <Typography
                style={{
                  color: colors.white,
                  marginLeft: 16,
                }}
                type="title3"
              >
                Back to Tour
              </Typography>
            </TouchableOpacity>
          </Header>
          <ScrollView
            style={{
              paddingHorizontal: 16,
              paddingVertical: 16,
              height: windowHeight - 50 - 32,
            }}
          >
            <View
              style={{
                paddingTop: 40,
              }}
            >
              <Typography
                type="title"
                style={{
                  marginBottom: 32,
                }}
              >
                {item.question}
              </Typography>

              {item.answers.map((answer, answerIndex) => (
                <View key={answerIndex}>
                  <TouchableOpacity
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginBottom: 24,
                    }}
                    onPress={() => {
                      setAnswer(tourFeedback.activeIndex, answerIndex);
                    }}
                  >
                    <View
                      style={[
                        {
                          width: 32,
                          height: 32,
                          borderRadius: 32,
                          borderColor: colors.gray300,
                          borderWidth: 1,
                          borderStyle: "solid",
                          backgroundColor: colors.white,
                          marginRight: 16,
                          justifyContent: "center",
                          alignItems: "center",
                        },
                        item.selected.includes(answerIndex)
                          ? {
                              backgroundColor: colors.primary,
                              shadowColor: "#2D3239",
                              shadowOpacity: 0.05,
                              shadowOffset: { width: 0, height: 4 },
                              shadowRadius: 4,
                            }
                          : {},
                      ]}
                    >
                      <Check size={16} color={colors.white} />
                    </View>
                    <Typography color="textDisabled">{answer}</Typography>
                  </TouchableOpacity>
                </View>
              ))}
              {item.selected.includes(item.answers.indexOf("Other")) && (
                <View>
                  <TextInput
                    maxLength={50}
                    value={item.additional}
                    onChangeText={(text) => {
                      handleAdditional(tourFeedback.activeIndex, text);
                    }}
                    style={{
                      borderColor: colors.gray300,
                      borderWidth: 1,
                      padding: 12,
                      fontSize: 16,
                      color: colors.gray900,
                    }}
                  />
                  <Typography>{item.additional.length}/50</Typography>
                </View>
              )}
            </View>
            <View
              style={{
                paddingVertical: 16,
                paddingHorizontal: 16,
                width: "100%",
              }}
            >
              {isLastPage ? (
                <Button
                  size="large"
                  onPress={() => setCompleted(true)}
                  disabled={!questionAnswered}
                  style={{
                    opacity: questionAnswered ? 1 : 0.8,
                  }}
                >
                  SEND FEEDBACK
                </Button>
              ) : (
                <Button
                  onPress={next}
                  disabled={!questionAnswered}
                  style={{ opacity: !questionAnswered ? 0.8 : 1 }}
                >
                  NEXT
                </Button>
              )}
            </View>
          </ScrollView>
        </>
      )}
    </>
  );
};

export default FeedbackModal;
